import { Component, Inject, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { StoriesService } from '../../features/stories/stories.service';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { ToastrService } from 'ngx-toastr';
import { NoDataComponent } from '../../shared/components/no-data/nodata.component';
import { TagsLanguageService } from '../../features/tags-language/tagsLanguage.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { PermissionDirective } from '../../shared/components/directives/permissions.directives';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { ContentLoaderComponent } from '../../shared/components/content-loader-component/contentLoader.component';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';

@Component({
  selector: 'app-stories-page',
  imports: [
    CommonModule,
    NoDataComponent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    PermissionDirective,
    ContentLoaderComponent,
  ],
  templateUrl: './stories-page.component.html',
  styleUrl: './stories-page.component.scss',
})
export class StoriesPageComponent {
  readonly dialog = inject(MatDialog);
  public storyList: any[] = [];
  public languages: any[] = [];
  public langCode: String = '';
  private searchSubject = new Subject<string>();
  noData = false;
  loading = false;
  searchTerm: string = '';

  constructor(
    private router: Router,
    private storiesService: StoriesService,
    private tagsService: TagsLanguageService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.getAllStories(this.langCode);
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.getAllStories(this.langCode, searchTerm);
      });
  }

  openDialog(story: any): void {
    const dialogRef = this.dialog.open(StoryModal, {
      panelClass: 'custom-story-panel',
      data: story,
    });
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searchSubject.next(searchTerm);
  }

  onImageError(event: Event) {
    const target = event.target as HTMLImageElement;
    target.src = 'assets/images/placeHolderImage.jpg';
  }

  newStory() {
    this.router.navigate(['stories/new']);
  }

  getLanguages(): void {
    this.tagsService.getLangauges().subscribe({
      next: (res) => {
        if (res?.data) {
          this.languages = res?.data;
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  onLanguageSelect(event: any) {
    this.langCode = event.value;
    this.getAllStories(this.langCode);
  }

  getAllStories(lang: any, search = '') {
    this.loading = true;
    this.storiesService.getStories(lang, search).subscribe({
      next: (res) => {
        if (res?.data) {
          this.storyList = res.data;
          if (this.storyList.length === 0) {
            this.noData = true;
          } else {
            this.noData = false;
          }
        }
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
      },
    });
  }

  getTranslationName(story: any): string {
    if (!story?.translations?.length) return '';
    const preferred = story.translations.find((t: any) => t.langId === 1);
    return (preferred?.name || story.translations[0].name).slice(0, 40);
  }

  onEditStory(id: any): void {
    this.router.navigate([`stories/edit/${id}`]);
  }

  onTranslateStory(id: any): void {
    this.router.navigate([`stories/translate-story/${id}`]);
  }

  deleteStory(id: any): void {
    const dialogRef = this.dialog.open(ConfirmDialog);

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.storiesService.removeStory(id).subscribe({
          next: (res) => {
            this.toastr.success(res?.message);
            this.getAllStories(this.langCode);
          },
          error: (err) => {
            console.log(err);
          },
        });
      }
    });
  }
}

@Component({
  selector: 'story-modal',
  templateUrl: './story-modal/story-modal.html',
  styleUrl: './story-modal/story-modal.component.scss',
  imports: [MatDialogContent],
})
export class StoryModal {
  readonly dialogRef = inject(MatDialogRef<StoryModal>);

  constructor(@Inject(MAT_DIALOG_DATA) public data: any) {}

  onNoClick(): void {
    this.dialogRef.close();
  }

  getDescription(story: any): string {
    if (!story?.translations?.length) return '';
    const preferred = story.translations.find((t: any) => t.langId === 1);
    return preferred?.description || story.translations[0].description;
  }
}
